import TokenService from './storage.service'
import router from '../router'
import axios from 'axios'
import store from '@/store/index'
import i18n from '@/plugins/i18n'
import { toast } from 'vue3-toastify'
import { TENANCY_SETTINGS } from '@/config'
import { forceLogoutToLogin, isPublicRoutePath, shouldForceLogoutOnApiError, canAttemptTokenRefresh } from '@/auth/session'
import { CHANGE_PASSWORD_PATH } from '@/auth/temporaryPassword'
import { refreshAccessToken, syncAuthHeaderFromStorage } from '@/auth/refresh'

const getLang = () => TokenService.getLang() || 'en'

const ApiService = {
  init(baseURL) {
    const tenantId = store.getters.tenantId
    const tenancyEnabled = store.getters.tenancyEnabled

    if (tenancyEnabled) {
      axios.defaults.headers.common['X-Tenant'] = tenantId
      localStorage.setItem('currentCountry', tenantId)
      baseURL = baseURL.replace('{tenant}', tenantId.toLowerCase())
      axios.defaults.headers.common['Accept-Language'] = getLang()
    } else {
      const lang = getLang()
      axios.defaults.headers.common['Accept-Language'] = lang
      axios.defaults.headers.common['GW-Language'] = lang
    }

    axios.defaults.baseURL = baseURL

    this.setupInterceptors()
    this.setupCrossTabTokenSync()
  },

  setupCrossTabTokenSync() {
    if (this._crossTabSyncRegistered || typeof window === 'undefined') {
      return
    }

    this._crossTabSyncRegistered = true
    window.addEventListener('storage', syncAuthHeaderFromStorage)
  },

  setupInterceptors() {
    if (this._interceptorsRegistered) {
      return
    }
    this._interceptorsRegistered = true

    axios.interceptors.request.use((config) => {
      store.dispatch('addPending', true)

      const token = TokenService.getToken()
      if (token) {
        config.headers.common.Authorization = `Bearer ${token}`
      }

      return config
    })

    axios.interceptors.response.use(
      (response) => {
        toast.clearAll()
        store.dispatch('addPending', false)
        const skipSuccessToast = response.config?.skipSuccessToast === true
        if (response.config.method !== 'get' && !skipSuccessToast) {
          this.showToast('success', 'message.toastMessages.success')
        }
        return response
      },
      async (error) => {
        store.dispatch('addPending', false)

        const originalConfig = error.config
        const currentPath = router.currentRoute.value.path

        if (originalConfig && error.response) {
          const requestUrl = originalConfig.url || ''
          const isAuthRequest =
            requestUrl.includes('auth/login') ||
            requestUrl.includes('auth/refresh') ||
            requestUrl.includes('auth/change-forced-password') ||
            requestUrl.includes('users/logout')

          if (!isAuthRequest && canAttemptTokenRefresh(error) && !originalConfig._retry) {
            originalConfig._retry = true

            try {
              const newToken = await refreshAccessToken()
              originalConfig.headers = originalConfig.headers || {}
              originalConfig.headers.common = originalConfig.headers.common || {}
              originalConfig.headers.Authorization = `Bearer ${newToken}`
              originalConfig.headers.common.Authorization = `Bearer ${newToken}`

              return axios(originalConfig)
            } catch (_refreshError) {
              if (!isPublicRoutePath(currentPath)) {
                forceLogoutToLogin({ redirectFrom: router.currentRoute.value.fullPath })
              }

              return Promise.reject(_refreshError)
            }
          }
        }

        const isSessionExpired = shouldForceLogoutOnApiError(error)
        const is401OnPublicPage = isSessionExpired && isPublicRoutePath(currentPath)

        const skipErrorToast = originalConfig?.skipErrorToast === true

        if (!isSessionExpired && !is401OnPublicPage && !skipErrorToast) {
          this.showToast('error', 'message.toastMessages.error')
        }

        if (isSessionExpired && !isPublicRoutePath(currentPath)) {
          forceLogoutToLogin({ redirectFrom: router.currentRoute.value.fullPath })
        }

        return Promise.reject(error)
      }
    )
  },

  showToast(type, messageKey) {
    toast(i18n.global.t(messageKey), {
      type,
      theme: 'colored',
      transition: toast.TRANSITIONS.SLIDE,
      position: toast.POSITION.TOP_RIGHT
    })
  },

  setHeader(contentType = 'application/json') {
    axios.defaults.headers.common['Content-Type'] = contentType
  },

  removeHeader() {
    delete axios.defaults.headers.common['Authorization']
    delete axios.defaults.headers.common['Content-Type']
  },

  csrfCookie() {
    return axios.get('/sanctum/csrf-cookie')
  },

  request(method, resource, data = null, headers = {}) {
    const lang = getLang()
    const urlPrefix = TENANCY_SETTINGS.TENANCY ? '/api/v1/admin/' : `/api/v1/${lang}/`
    const url = urlPrefix + resource
    const skipSuccessToast = headers?.skipSuccessToast === true
    const skipErrorToast = headers?.skipErrorToast === true

    if (headers?.skipSuccessToast !== undefined) {
      const { skipSuccessToast: _skipSuccessToast, ...restHeaders } = headers
      headers = restHeaders
    }

    if (headers?.skipErrorToast !== undefined) {
      const { skipErrorToast: _skipErrorToast, ...restHeaders } = headers
      headers = restHeaders
    }

    const config = { method, url, data, headers, skipSuccessToast, skipErrorToast }

    if (headers && headers.responseType) {
      config.responseType = headers.responseType
      delete headers.responseType
    }

    return axios(config)
  },

  get(resource) {
    return this.request('get', resource)
  },

  post(resource, data, headers) {
    return this.request('post', resource, data, headers)
  },

  put(resource, data, headers) {
    return this.request('put', resource, data, headers)
  },

  delete(resource, data = null) {
    return this.request('delete', resource, data)
  },

  patch(resource, data) {
    return this.request('patch', resource, data)
  },

  getFile(resource, options = {}) {
    return this.request('get', resource, null, { responseType: 'blob', ...options })
  }
}

export default ApiService
